import React, { useState, useEffect } from "react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import {
  FileText,
  Search,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
} from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getToken } from "../utils/auth";

const STATUS_CONFIG = {
  RECEIVED: {
    label: "Pending",
    color: "bg-yellow-100 text-yellow-800 border border-yellow-200",
    icon: <Clock className="w-3.5 h-3.5" />,
  },
  APPROVED: {
    label: "Approved",
    color: "bg-green-100 text-green-800 border border-green-200",
    icon: <CheckCircle className="w-3.5 h-3.5" />,
  },
  REJECTED: {
    label: "Rejected",
    color: "bg-red-100 text-red-800 border border-red-200",
    icon: <XCircle className="w-3.5 h-3.5" />,
  },
};

export default function HliStudentClaims() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["hliStudentClaims", currentPage],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}hec/studentClaim/getClaimsByMyInstitution?page=${currentPage}&size=${pageSize}`,
        { headers: { Authorization: "Bearer " + getToken() } }
      );
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `HTTP ${res.status}`);
      }
      return res.json();
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error("Failed to load claims: " + error.message);
    }
  }, [isError, error]);

  const claims = data?.content || [];
  const totalPages = data?.totalPages || 0;

  const filtered = claims.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.studentNumber || "").toLowerCase().includes(term) ||
      (c.claimType || "").toLowerCase().includes(term) ||
      (c.claimDescription || "").toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>
      <div className="p-6 sm:ml-64 flex-1 flex flex-col">
        <div className="mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-teal-600 rounded-2xl shadow-xl p-8 mb-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Student Claims</h1>
                <p className="text-cyan-100 text-sm mt-1">
                  View student claims from your institution
                </p>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-white rounded-xl shadow border border-gray-200 p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by reg number, type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700 transition disabled:opacity-50"
              >
                {isFetching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Refresh
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-20 gap-3 text-cyan-600">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading claims...</span>
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center py-20 text-red-500 gap-2">
                <XCircle className="w-5 h-5" />
                <span>Error: {error?.message}</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
                <FileText className="w-12 h-12 opacity-30" />
                <p className="text-lg font-medium">No claims found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {["#", "Reg Number", "Claim Type", "Description", "Academic Year", "Status", "Submitted At"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((claim, idx) => {
                      const status = claim.claimStatus || "RECEIVED";
                      const cfg = STATUS_CONFIG[status] || STATUS_CONFIG["RECEIVED"];
                      return (
                        <tr
                          key={claim.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3 text-gray-500 font-medium">
                            {currentPage * pageSize + idx + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800">
                            {claim.studentNumber || "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {claim.claimType || "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-xs truncate">
                            {claim.claimDescription || "—"}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {claim.academicYear?.academicYearCode || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}
                            >
                              {cfg.icon}
                              {cfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {claim.createdAt
                              ? new Date(claim.createdAt).toLocaleDateString()
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <span className="text-sm text-gray-600">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
